// src/components/pos/Returns.jsx
// POS Returns Component - Individual popup component for return management

import { useState, useEffect, useCallback, useRef } from 'react'
import { RotateCcw, X, Search, Calendar, RefreshCw, DollarSign, FileText, AlertCircle, CheckCircle, Package } from 'lucide-react'
import { cn } from '../../utils/cn'
import { POSButton } from './ui/Button'
import { SearchInput } from './ui/Input'
import returnService from '../../services/returnService'
import orderService from '../../services/orderService'
import Toast from '../common/Toast'
import InlineLoader from '../ui/InlineLoader'

const Returns = ({ className, ...props }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTab, setSelectedTab] = useState('process') // process, history
  const [loading, setLoading] = useState(false)
  const [orderLoading, setOrderLoading] = useState(false)
  
  // Return form state
  const [orderNumberInput, setOrderNumberInput] = useState('')
  const [orderSuggestions, setOrderSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestionLoading, setSuggestionLoading] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [returnType, setReturnType] = useState('customer')
  const [refundMethod, setRefundMethod] = useState('cash')
  const [reasonType, setReasonType] = useState('change_mind')
  const [selectedItems, setSelectedItems] = useState([])
  
  // Refs
  const dropdownRef = useRef(null)
  
  // Return history state
  const [returnHistory, setReturnHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  
  // View details modal state
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false)
  const [selectedReturnDetails, setSelectedReturnDetails] = useState(null)
  
  // Toast state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })

  // Get available options
  const returnReasons = returnService.getReturnReasons()
  const returnTypes = returnService.getReturnTypes()
  const refundMethods = returnService.getRefundMethods()

  // Show toast message
  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
  }, [])

  // Load return history
  const loadReturnHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      console.log('📋 Loading return history...')
      const response = await returnService.getAllOrderReturns()
      
      if (response.success && response.data) {
        console.log('✅ Return history loaded:', response.data)
        // Handle nested data structure - extract orderReturns array
        const returnsData = response.data.orderReturns || response.data
        setReturnHistory(Array.isArray(returnsData) ? returnsData : [])
        console.log('📊 Setting return history:', returnsData)
      } else {
        console.log('⚠️ No return history found')
        setReturnHistory([])
      }
    } catch (error) {
      console.error('❌ Error loading return history:', error)
      
      // Handle different error scenarios
      if (error.response?.status === 404) {
        console.log('📝 No returns found (404)')
        setReturnHistory([])
      } else if (error.response?.status === 403) {
        showToast('Access denied. You may not have permission to view return history', 'error')
        setReturnHistory([])
      } else if (error.message?.includes('Network Error')) {
        showToast('Network error. Please check your connection', 'error')
        setReturnHistory([])
      } else {
        showToast('Failed to load return history', 'error')
        setReturnHistory([])
      }
    }
    setHistoryLoading(false)
  }, [showToast])

  // Load return history when tab changes
  useEffect(() => {
    if (selectedTab === 'history' && isOpen) {
      loadReturnHistory()
    }
  }, [selectedTab, isOpen, loadReturnHistory])

  // Search for order suggestions by partial order number
  const searchOrderSuggestions = useCallback(async (partialNumber) => {
    if (!partialNumber || partialNumber.length < 3) {
      setOrderSuggestions([])
      setShowSuggestions(false)
      return
    }

    setSuggestionLoading(true)
    try {
      console.log('🔍 Searching for order suggestions:', partialNumber)
      const response = await orderService.searchCompletedOrderNumbers(partialNumber, 10)
      
      if (response.success && response.data?.orders) {
        console.log('✅ Order suggestions loaded:', response.data.orders)
        setOrderSuggestions(response.data.orders)
        setShowSuggestions(true)
      } else {
        setOrderSuggestions([])
        setShowSuggestions(false)
      }
    } catch (error) {
      console.error('❌ Error loading order suggestions:', error)
      setOrderSuggestions([])
      setShowSuggestions(false)
    }
    setSuggestionLoading(false)
  }, [])

  // Debounced search for order suggestions
  useEffect(() => {
    // Don't search for suggestions if we already have a selected order
    if (selectedOrder) {
      return
    }

    const timeoutId = setTimeout(() => {
      searchOrderSuggestions(orderNumberInput)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [orderNumberInput, searchOrderSuggestions, selectedOrder])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false)
        setOrderSuggestions([])
      }
    }

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSuggestions])

  // Search for order by order number
  const searchOrder = async (orderNumber = null) => {
    const searchValue = orderNumber || orderNumberInput.trim()
    
    if (!searchValue) {
      showToast('Please enter an order number', 'error')
      return
    }

    setOrderLoading(true)
    setShowSuggestions(false)
    try {
      console.log('🔍 Searching for order by number:', searchValue)
      const response = await orderService.searchOrderByNumber(searchValue)
      
      if (response.success && response.data?.order) {
        const order = response.data.order
        console.log('🔍 Order data retrieved:', order)
        console.log('📊 Order status:', order.orderStatus)
        console.log('💳 Payment status:', order.paymentStatus)
        
        // Validate that the order can be returned
        if (order.orderStatus !== 'completed') {
          console.log('❌ Order status validation failed. Expected: "completed", Got:', order.orderStatus)
          showToast(`Order status is "${order.orderStatus}". Only completed orders can be returned`, 'error')
          setOrderLoading(false)
          return
        }

        if (order.paymentStatus !== 'paid') {
          console.log('❌ Payment status validation failed. Expected: "paid", Got:', order.paymentStatus)
          showToast(`Payment status is "${order.paymentStatus}". Only paid orders can be returned`, 'error')
          setOrderLoading(false)
          return
        }

        console.log('✅ Order validation passed, setting selected order')
        setSelectedOrder(order)
        setSelectedItems([])
        // Explicitly hide dropdown and clear suggestions
        setShowSuggestions(false)
        setOrderSuggestions([])
        showToast('Order loaded successfully', 'success')
        console.log('✅ Order loaded:', order)
      } else {
        showToast('Order not found', 'error')
      }
    } catch (error) {
      console.error('❌ Error searching order:', error)
      
      // Check if it's a 404 error (order not found)
      if (error.response?.status === 404) {
        showToast('Order not found', 'error')
      } else if (error.response?.status === 403) {
        showToast('Access denied. You may not have permission to view this order', 'error')
      } else if (error.message?.includes('Network Error')) {
        showToast('Network error. Please check your connection', 'error')
      } else {
        showToast('Failed to load order. Please try again', 'error')
      }
    }
    setOrderLoading(false)
  }

  // Handle order number input change
  const handleOrderNumberChange = (e) => {
    const value = e.target.value
    setOrderNumberInput(value)
    
    // Clear selected order if input is cleared or changed from the selected order
    if (!value.trim()) {
      setSelectedOrder(null)
      setSelectedItems([])
      setShowSuggestions(false)
      setOrderSuggestions([])
    } else if (selectedOrder && value !== selectedOrder.orderNumber) {
      // User is typing something different from the selected order
      setSelectedOrder(null)
      setSelectedItems([])
    }
  }

  // Handle suggestion selection
  const handleSuggestionSelect = (order) => {
    setOrderNumberInput(order.orderNumber)
    setOrderSuggestions([])
    setShowSuggestions(false)
    searchOrder(order.orderNumber)
  }

  // Toggle item selection for return
  const toggleItemSelection = (item) => {
    setSelectedItems(prev => {
      const existingItem = prev.find(i => i._id === item._id)
      if (existingItem) {
        return prev.filter(i => i._id !== item._id)
      } else {
        return [...prev, { 
          ...item, 
          returnQuantity: 1,
          maxQuantity: item.quantity 
        }]
      }
    })
  }

  // Update return quantity for selected item
  const updateReturnQuantity = (itemId, quantity) => {
    setSelectedItems(prev => 
      prev.map(item => 
        item._id === itemId 
          ? { ...item, returnQuantity: Math.min(Math.max(1, quantity), item.maxQuantity) }
          : item
      )
    )
  }

  // Calculate total refund amount
  const getTotalRefund = () => {
    return selectedItems.reduce((total, item) => {
      const unitPrice = item.promotionEngineResult?.unitDetails?.finalPricePerUnit || item.unitPrice
      return total + (unitPrice * item.returnQuantity)
    }, 0)
  }

  // Process return
  const processReturn = async () => {
    if (!selectedOrder) {
      showToast('Please search and select an order first', 'error')
      return
    }

    if (selectedItems.length === 0) {
      showToast('Please select items to return', 'error')
      return
    }

    setLoading(true)
    try {
      const returnData = {
        orderId: selectedOrder._id,
        returnType,
        refundMethod,
        items: selectedItems.map(item => ({
          productId: item.product._id,
          returnQuantity: item.returnQuantity
        })),
        reasonType
      }

      console.log('🔄 Processing return with data:', returnData)
      const response = await returnService.createOrderReturn(returnData)
      
      if (response.success) {
        showToast('Return processed successfully!', 'success')
        console.log('✅ Return processed:', response.data)
        
        // Reset form
        setOrderNumberInput('')
        setSelectedOrder(null)
        setSelectedItems([])
        setReturnType('customer')
        setRefundMethod('cash')
        setReasonType('change_mind')
        
        // Refresh history if on history tab
        if (selectedTab === 'history') {
          loadReturnHistory()
        }
      } else {
        showToast(response.message || 'Failed to process return', 'error')
      }
    } catch (error) {
      console.error('❌ Error processing return:', error)
      
      // Handle different error scenarios
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || 'Invalid return data'
        showToast(errorMessage, 'error')
      } else if (error.response?.status === 404) {
        showToast('Order not found or cannot be returned', 'error')
      } else if (error.response?.status === 403) {
        showToast('Access denied. You may not have permission to process returns', 'error')
      } else if (error.message?.includes('Network Error')) {
        showToast('Network error. Please check your connection', 'error')
      } else {
        showToast('Failed to process return. Please try again.', 'error')
      }
    }
    setLoading(false)
  }

  // Format currency
  const formatCurrency = (amount) => {
    return `Rs.${amount?.toFixed(2) || '0.00'}`
  }

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // View return details
  const viewReturnDetails = (returnItem) => {
    setSelectedReturnDetails(returnItem)
    setViewDetailsOpen(true)
  }

  return (
    <>
      {/* Trigger Button */}
      <POSButton
        variant="default"
        className={cn(
          "h-20 flex-col space-y-2 bg-gray-900 text-white hover:bg-gray-600 border-gray-700",
          className
        )}
        onClick={() => setIsOpen(true)}
        {...props}
      >
        <RotateCcw className="h-6 w-6" />
        <span className="text-sm font-medium">Returns</span>
      </POSButton>

      {/* Returns Popup */}
      {isOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-orange-600 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <RotateCcw className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Returns Management</h3>
                  <p className="text-sm text-orange-100">Process returns and refunds</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 flex-shrink-0">
              <div className="flex">
                <button
                  onClick={() => setSelectedTab('process')}
                  className={cn(
                    "px-6 py-3 font-medium text-sm border-b-2 transition-colors",
                    selectedTab === 'process'
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  )}
                >
                  Process Return
                </button>
                <button
                  onClick={() => setSelectedTab('history')}
                  className={cn(
                    "px-6 py-3 font-medium text-sm border-b-2 transition-colors",
                    selectedTab === 'history'
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  )}
                >
                  Return History
                </button>
              </div>
            </div>

            {/* Content - Scrollable Area */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedTab === 'process' ? (
                <div className="space-y-6">
                  {/* Order Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order Number
                    </label>
                    <div className="relative">
                      <div className="flex space-x-3">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={orderNumberInput}
                            onChange={handleOrderNumberChange}
                            placeholder="Enter order number (e.g., ORD02280504001)..."
                            className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                            onKeyDown={(e) => e.key === 'Enter' && searchOrder()}
                          />
                          
                          {/* Order suggestions dropdown */}
                          {showSuggestions && orderSuggestions.length > 0 && !selectedOrder && (
                            <div ref={dropdownRef} className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {suggestionLoading && (
                                <div className="p-3 text-center text-gray-500">
                                  <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                                  Loading suggestions...
                                </div>
                              )}
                              {!suggestionLoading && orderSuggestions.map((order) => (
                                <button
                                  key={order._id}
                                  onClick={() => handleSuggestionSelect(order)}
                                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-orange-50"
                                >
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <div className="font-medium text-gray-900">{order.orderNumber}</div>
                                      <div className="text-sm text-gray-500">
                                        {order.customer?.name || 'Walk-in'} • 
                                        {new Date(order.createdAt).toLocaleDateString()} • 
                                      </div>
                                    </div>
                                    <div className="text-xs text-orange-600 font-medium">
                                      {order.orderType}
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <POSButton 
                          variant="primary" 
                          leftIcon={Search}
                          onClick={() => searchOrder()}
                          loading={orderLoading}
                          disabled={!orderNumberInput.trim()}
                        >
                          Search
                        </POSButton>
                      </div>
                    </div>
                  </div>

                  {/* Order Details */}
                  {selectedOrder && (
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <h4 className="font-medium text-gray-900 mb-3">Order Details</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-gray-500">Order Number:</span>
                          <span className="ml-2 font-medium">{selectedOrder.orderNumber}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Customer:</span>
                          <span className="ml-2 font-medium">{selectedOrder.customer?.name || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Date:</span>
                          <span className="ml-2 font-medium">{formatDate(selectedOrder.orderDate)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Total:</span>
                          <span className="ml-2 font-medium">{formatCurrency(selectedOrder.orderPayment?.totalAmount)}</span>
                        </div>
                      </div>

                      {/* Return Configuration */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Return Type
                          </label>
                          <select
                            value={returnType}
                            onChange={(e) => setReturnType(e.target.value)}
                            className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                          >
                            {returnTypes.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Refund Method
                          </label>
                          <select
                            value={refundMethod}
                            onChange={(e) => setRefundMethod(e.target.value)}
                            className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                          >
                            {refundMethods.map(method => (
                              <option key={method.value} value={method.value}>
                                {method.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reason
                          </label>
                          <select
                            value={reasonType}
                            onChange={(e) => setReasonType(e.target.value)}
                            className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                          >
                            {returnReasons.map(reason => (
                              <option key={reason.value} value={reason.value}>
                                {reason.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Items Selection */}
                      <h5 className="font-medium text-gray-900 mb-3">Select Items to Return</h5>
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                        {selectedOrder.orderItems?.map(item => {
                          const selectedItem = selectedItems.find(i => i._id === item._id)
                          const unitPrice = item.promotionEngineResult?.unitDetails?.finalPricePerUnit || item.unitPrice
                          
                          return (
                            <div key={item._id} className="flex items-center justify-between p-3 bg-white rounded border">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={!!selectedItem}
                                  onChange={() => toggleItemSelection(item)}
                                  className="h-4 w-4 text-orange-600 rounded"
                                />
                                <div>
                                  <p className="font-medium">{item.product?.name}</p>
                                  <p className="text-sm text-gray-500">
                                    SKU: {item.product?.sku} | Available: {item.quantity} | {formatCurrency(unitPrice)} each
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3">
                                {selectedItem && (
                                  <div className="flex items-center space-x-2">
                                    <label className="text-sm text-gray-600">Qty:</label>
                                    <input
                                      type="number"
                                      min="1"
                                      max={item.quantity}
                                      value={selectedItem.returnQuantity}
                                      onChange={(e) => updateReturnQuantity(item._id, parseInt(e.target.value))}
                                      className="w-16 h-8 px-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-orange-500"
                                    />
                                  </div>
                                )}
                                <p className="font-medium">{formatCurrency(unitPrice * item.quantity)}</p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Refund Summary */}
                  {selectedItems.length > 0 && (
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <h5 className="font-medium text-orange-900 mb-2">Refund Summary</h5>
                      <div className="space-y-2">
                        {selectedItems.map(item => {
                          const unitPrice = item.promotionEngineResult?.unitDetails?.finalPricePerUnit || item.unitPrice
                          return (
                            <div key={item._id} className="flex justify-between text-sm">
                              <span>{item.product?.name} (x{item.returnQuantity})</span>
                              <span>{formatCurrency(unitPrice * item.returnQuantity)}</span>
                            </div>
                          )
                        })}
                        <div className="border-t border-orange-300 pt-2 flex justify-between font-medium text-orange-900">
                          <span>Total Refund:</span>
                          <span>{formatCurrency(getTotalRefund())}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  <POSButton
                    variant="primary"
                    className="w-full"
                    onClick={processReturn}
                    leftIcon={RefreshCw}
                    loading={loading}
                    disabled={!selectedOrder || selectedItems.length === 0}
                  >
                    Process Return
                  </POSButton>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Search */}
                  <SearchInput
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClear={() => setSearchTerm('')}
                    placeholder="Search returns by order number..."
                  />

                  {/* Return History List */}
                  {historyLoading ? (
                    <InlineLoader title="Loading returns..." />
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                      {returnHistory
                        .filter(ret => 
                          ret.order?.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          ret.order?.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .map(returnItem => (
                          <div key={returnItem._id} className="bg-gray-50 p-4 rounded-lg border">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-medium text-gray-900">Return #{returnItem._id}</h4>
                                <p className="text-sm text-gray-500">
                                  Order: {returnItem.order?.orderNumber} | Customer: {returnItem.order?.customer?.name || 'N/A'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className={cn(
                                  "px-2 py-1 rounded-full text-xs font-medium",
                                  returnItem.returnType === 'customer'
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-orange-100 text-orange-800"
                                )}>
                                  {returnItem.returnType}
                                </span>
                                <span className={cn(
                                  "px-2 py-1 rounded-full text-xs font-medium",
                                  returnItem.refundMethod === 'cash'
                                    ? "bg-green-100 text-green-800"
                                    : "bg-orange-100 text-orange-800"
                                )}>
                                  {returnItem.refundMethod}
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                              <div>
                                <span className="text-gray-500">Date:</span>
                                <span className="ml-2">{formatDate(returnItem.createdAt)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Refund Amount:</span>
                                <span className="ml-2 font-medium">{formatCurrency(returnItem.totalAmount)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Processed by:</span>
                                <span className="ml-2">{returnItem.createdBy?.username || 'System'}</span>
                              </div>
                            </div>

                            <div className="text-sm mb-2">
                              <span className="text-gray-500">Items:</span>
                              <span className="ml-2">
                                {returnItem.items?.map(item => 
                                  `${item.product?.name || 'Unknown'} (${item.returnQuantity})`
                                ).join(', ')}
                              </span>
                            </div>

                            <div className="text-sm mb-3">
                              <span className="text-gray-500">Reason:</span>
                              <span className="ml-2 capitalize">{returnItem.reasonType?.replace('_', ' ')}</span>
                            </div>

                            <div className="flex space-x-2">
                              <POSButton 
                                size="sm" 
                                variant="outline" 
                                leftIcon={FileText}
                                onClick={() => viewReturnDetails(returnItem)}
                              >
                                View Details
                              </POSButton>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )}

                  {returnHistory.length === 0 && !historyLoading && (
                    <div className="text-center py-8">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No return history found</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewDetailsOpen && selectedReturnDetails && (
        <div className="fixed inset-0 z-70 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setViewDetailsOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-orange-600 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Return Details</h3>
                  <p className="text-sm text-orange-100">Return #{selectedReturnDetails._id}</p>
                </div>
              </div>
              <button
                onClick={() => setViewDetailsOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Order Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Order Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Order Number:</span>
                      <span className="ml-2 font-medium">{selectedReturnDetails.order?.orderNumber || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Customer:</span>
                      <span className="ml-2 font-medium">{selectedReturnDetails.order?.customer?.name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Order Date:</span>
                      <span className="ml-2 font-medium">{formatDate(selectedReturnDetails.order?.orderDate)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Original Amount:</span>
                      <span className="ml-2 font-medium">{formatCurrency(selectedReturnDetails.order?.orderPayment?.totalAmount)}</span>
                    </div>
                  </div>
                </div>

                {/* Return Information */}
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <h4 className="font-medium text-orange-900 mb-3">Return Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-gray-500">Return Date:</span>
                      <span className="ml-2 font-medium">{formatDate(selectedReturnDetails.createdAt)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Refund Amount:</span>
                      <span className="ml-2 font-medium text-orange-700">{formatCurrency(selectedReturnDetails.totalAmount)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Return Type:</span>
                      <span className="ml-2">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {selectedReturnDetails.returnType}
                        </span>
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Refund Method:</span>
                      <span className="ml-2">
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium",
                          selectedReturnDetails.refundMethod === 'cash'
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        )}>
                          {selectedReturnDetails.refundMethod}
                        </span>
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Reason:</span>
                      <span className="ml-2 font-medium capitalize">{selectedReturnDetails.reasonType?.replace('_', ' ')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Processed by:</span>
                      <span className="ml-2 font-medium">{selectedReturnDetails.createdBy?.username || 'System'}</span>
                    </div>
                  </div>
                </div>

                {/* Returned Items */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Returned Items</h4>
                  <div className="space-y-3">
                    {selectedReturnDetails.items?.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div className="flex items-center space-x-3">
                          <div>
                            <p className="font-medium">{item.product?.name || 'Unknown Product'}</p>
                            <p className="text-sm text-gray-500">
                              SKU: {item.product?.sku || 'N/A'} | Quantity Returned: {item.returnQuantity}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatCurrency(item.unitPrice || 0)}</p>
                          <p className="text-sm text-gray-500">per unit</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Notes */}
                {selectedReturnDetails.notes && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Additional Notes</h4>
                    <div className="p-3 bg-gray-50 rounded border">
                      <p className="text-sm text-gray-700">{selectedReturnDetails.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <POSButton
                variant="outline"
                onClick={() => setViewDetailsOpen(false)}
              >
                Close
              </POSButton>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </>
  )
}

export default Returns